/* @img-migrated */
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Gift,
  Search,
  Building2,
  Globe,
  Phone,
  Mail,
  Filter,
  Tag,
  Info,
  ExternalLink,
  Lock,
  ArrowUpRight,
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { MembershipRelationsCard } from '../../components/MembershipRelationsCard';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const resolveUrl = (url) =>
  resolveImg(url);

const CATEGORIES = [
  'Professional Services',
  'Food & Restaurants',
  'Retail',
  'Health & Wellness',
  'Beauty & Personal Care',
  'Home & Services',
  'Legal & Financial',
  'Marketing & Creative',
  'Technology',
  'Travel & Hospitality',
  'Education & Training',
  'Other',
];

const PortalMemberDeals = () => {
  const [benefits, setBenefits] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [allowed, setAllowed] = useState(true);
  const [tier, setTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');

  useEffect(() => {
    fetch();
     
  }, []);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/portal/member-benefits`);
      setBenefits(res.data?.benefits || []);
      setEnabled(res.data?.enabled !== false);
      setAllowed(res.data?.allowed !== false);
      setTier(res.data?.tier || null);
    } catch { /* */ } finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    let list = benefits;
    if (category) list = list.filter((b) => b.discount_category === category);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (b) =>
          (b.company_name || '').toLowerCase().includes(q) ||
          (b.discount_description || '').toLowerCase().includes(q) ||
          (b.industry || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [benefits, search, category]);

  if (!enabled) {
    return (
      <div className="p-6 lg:p-8" data-testid="member-deals-disabled">
        <div className="max-w-xl mx-auto bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
            <Info className="w-6 h-6 text-slate-400" />
          </div>
          <h2 className="text-lg font-bold text-[#002249]">Member Deals temporarily unavailable</h2>
          <p className="text-[13px] text-slate-500 mt-1.5">
            A Super Admin has disabled the Member Benefits directory. Check back later.
          </p>
        </div>
      </div>
    );
  }

  // Premium "Access Required" state — shown when the user's tier or per-user
  // override does not grant access. Mirrors Apple/Stripe upgrade gates.
  if (!allowed) {
    return (
      <div className="p-4 sm:p-6 lg:p-10" data-testid="member-deals-access-required">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-[0_2px_12px_rgba(0,0,0,0.05)] overflow-hidden"
          >
            <div className="px-6 sm:px-10 pt-10 pb-8 bg-gradient-to-br from-[#002249] via-[#001530] to-[#000B1E] text-white text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 mx-auto">
                <Lock className="w-7 h-7 text-[#E63946]" />
              </div>
              <p className="mt-4 text-[10.5px] font-bold uppercase tracking-[0.2em] text-[#E63946]">Member Deals Access Required</p>
              <h1 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight">
                This feature is available for selected membership tiers.
              </h1>
              <p className="mt-3 text-[14px] text-white/75 max-w-md mx-auto leading-relaxed">
                Upgrade your membership or reach out to Membership Relations to learn how to unlock exclusive
                member-to-member discounts.
              </p>
              {tier && (
                <p className="mt-4 inline-block text-[11px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full bg-white/10 text-white/80">
                  Your current tier: {tier}
                </p>
              )}
            </div>
            <div className="px-6 sm:px-10 py-6 bg-white">
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/membership"
                  className="flex-1 inline-flex items-center justify-center gap-2 h-12 px-5 rounded-lg bg-[#E63946] hover:bg-[#c02a35] text-white text-[14px] font-semibold transition-colors"
                  data-testid="member-deals-upgrade-cta"
                >
                  <ArrowUpRight className="w-4 h-4" />Upgrade Membership
                </Link>
                <a
                  href="mailto:membership@dominicanchamberfl.org?subject=Member%20Deals%20access%20request"
                  className="flex-1 inline-flex items-center justify-center gap-2 h-12 px-5 rounded-lg border-2 border-[#002249] text-[#002249] hover:bg-[#002249]/5 text-[14px] font-semibold transition-colors"
                  data-testid="member-deals-contact-cta"
                >
                  <Mail className="w-4 h-4" />Contact Membership
                </a>
              </div>
            </div>
          </motion.div>

          <div className="mt-6">
            <MembershipRelationsCard
              variant="default"
              body="Have questions about your tier or want to discuss the right plan for your business? Our team responds within one business day."
              showSecondary={false}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="portal-member-deals">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
            <Gift className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#002249]">Member Deals</h1>
            <p className="text-slate-500 text-sm mt-1 max-w-xl">
              Exclusive discounts and perks offered by fellow DCCF members. When you connect, mention
              you're a DCCF member to redeem.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4 mb-6 flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search company, perk or industry..."
            className="h-11 pl-9 rounded-lg"
            data-testid="deals-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400 hidden sm:block" />
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="h-11 px-3 rounded-lg border border-slate-200 bg-white text-[14px] text-slate-700"
            data-testid="deals-category"
          >
            <option value="">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-48 bg-white border border-slate-200 rounded-xl skeleton" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
          <Gift className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">
            {benefits.length === 0
              ? 'No member deals yet — be the first to offer one from your Company Profile.'
              : 'No deals match your filters.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((b, i) => (
            <DealCard key={b.user_id || i} b={b} index={i} />
          ))}
        </div>
      )}
    </div>
  );
};

const DealCard = ({ b, index }) => {
  const isDeal = b.source === 'deal';
  const title = isDeal ? b.title : (b.company_name || 'Member Business');
  const subtitle = isDeal ? (b.company_name || b.member_name || '') : (b.industry || '');
  const ctaHref = isDeal && b.cta_url
    ? (b.cta_url.startsWith('http') ? b.cta_url : `https://${b.cta_url}`)
    : (b.email
        ? `mailto:${b.email}?subject=DCCF%20Member%20Deal%20-%20${encodeURIComponent(b.company_name || '')}`
        : (b.phone ? `tel:${b.phone}` : b.website));
  const ctaLabel = isDeal ? (b.cta_text || 'Redeem Offer') : 'Contact Member';
  const trackClick = () => {
    if (isDeal && b.deal_id) {
      try { axios.post(`${API}/portal/member-deals/${b.deal_id}/click`); } catch { /* */ }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.2) }}
      className={`bg-white border ${b.featured ? 'border-amber-300 shadow-[0_0_0_1px_rgba(245,158,11,0.15)]' : 'border-slate-200'} rounded-xl overflow-hidden hover:border-[#002249]/40 hover:shadow-sm transition-all flex flex-col`}
      data-testid={`deal-card-${index}`}
    >
      {isDeal && b.image_url && (
        <div className="h-32 bg-slate-100 overflow-hidden">
          <Img variant="generic" src={resolveUrl(b.image_url)} alt={title} className="w-full h-full object-cover" loading="lazy" />
        </div>
      )}
      <div className="p-4 flex items-start gap-3">
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center flex-shrink-0">
          {b.logo_url && !isDeal ? (
            <Img variant="business" src={resolveUrl(b.logo_url)} alt={b.company_name} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <Building2 className="w-5 h-5 text-slate-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <h3 className="text-[15px] font-semibold text-[#002249] truncate" title={title}>
              {title}
            </h3>
            {b.featured && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[9.5px] font-bold uppercase tracking-wide">★ Featured</span>
            )}
          </div>
          {subtitle && <p className="text-[11px] text-slate-500 mt-0.5 truncate">{subtitle}</p>}
        </div>
      </div>

      <div className="px-4 pb-4 flex-1 flex flex-col">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold uppercase tracking-wide self-start mb-2">
          <Gift className="w-3 h-3" /> Member Discount
        </span>
        <p className="text-[13px] text-slate-700 leading-relaxed line-clamp-4 flex-1">
          {b.discount_description}
        </p>
        {b.discount_category && (
          <p className="text-[11px] text-slate-400 mt-2 inline-flex items-center gap-1">
            <Tag className="w-3 h-3" /> {b.discount_category}
          </p>
        )}
        {b.discount_contact_method && (
          <p className="text-[12px] text-slate-500 mt-2 italic">
            How to redeem: {b.discount_contact_method}
          </p>
        )}
        {isDeal && b.end_at && (
          <p className="text-[11px] text-amber-700 mt-2 inline-flex items-center gap-1">
            Expires {b.end_at.slice(0, 10)}
          </p>
        )}
      </div>

      <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center gap-2">
        {ctaHref ? (
          <a
            href={ctaHref}
            target={ctaHref?.startsWith('http') ? '_blank' : undefined}
            rel="noopener noreferrer"
            onClick={trackClick}
            className="flex-1"
            data-testid={`deal-contact-${index}`}
          >
            <Button className="w-full h-9 rounded-lg bg-[#002249] hover:bg-[#0a3570] text-[13px]">
              {ctaLabel}
              <ExternalLink className="w-3.5 h-3.5 ml-1.5 opacity-80" />
            </Button>
          </a>
        ) : (
          <Button disabled className="w-full h-9 rounded-lg text-[13px]">
            No contact listed
          </Button>
        )}
        {b.website && !isDeal && (
          <a
            href={b.website.startsWith('http') ? b.website : `https://${b.website}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0"
            title="Website"
            data-testid={`deal-website-${index}`}
          >
            <Button variant="outline" className="h-9 w-9 p-0 rounded-lg">
              <Globe className="w-3.5 h-3.5" />
            </Button>
          </a>
        )}
      </div>
    </motion.div>
  );
};

export default PortalMemberDeals;
